import { MemberLevelReport } from './MemberLevelReport';
import { MemberNotificationsPanel } from './MemberNotificationsPanel';
import { MemberTagExport, MemberTagFilter, MemberTags } from './MemberTags';
/**
 * @page MemberListPage
 * @description 會員列表頁（UI-3-1）
 * 分頁、等級/狀態篩選、模糊搜尋
 */

import { ZipaDailyTotals } from './ZipaDailyTotals';
import { LevelBadge, StatusBadge } from './memberBadges';
import { useAuthStore } from '../../store/auth.store';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import type {
  MemberDto,
  PaginatedMembersDto,
  MemberLevelDto,
  MemberStatus,
} from '@pokerroom/types';

const PAGE_SIZE = 20;

const DEFAULT_TAG_FILTER = {
  tagIds: [] as string[],
  tagMatch: 'ANY' as 'ANY' | 'ALL',
  tagScope: 'EFFECTIVE' as 'EFFECTIVE' | 'ASSOCIATION' | 'CURRENT_VENUE',
};

const debtAmount = (totalDebt: string) => {
  const value = parseFloat(totalDebt);
  return Number.isFinite(value) && value > 0 ? value : null;
};

export default function MemberListPage() {
  const { t } = useTranslation('member');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const canReadReservations = useAuthStore((s) => s.allowedActions?.includes('reservation:read'));
  const canRead = useAuthStore((s) => s.allowedActions?.includes('member:read'));

  const [tagFilter, setTagFilter] = useState(DEFAULT_TAG_FILTER);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [levelId, setLevelId] = useState('');
  const [status, setStatus] = useState<MemberStatus | ''>('');
  const [manageTags, setManageTags] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const canManageNotifications = useAuthStore(
    (s) => s.allowedActions?.includes('member:notifications_manage') ?? false,
  );
  const canSendNotifications = useAuthStore(
    (s) => s.allowedActions?.includes('member:notifications_send') ?? false,
  );
  const notificationSession = useAuthStore((s) => s.sessionRevision);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: levels = [] } = useQuery<MemberLevelDto[]>({
    queryKey: ['member-levels'],
    queryFn: () => api.get<MemberLevelDto[]>('/members/levels'),
  });

  const { data, isLoading, isError, isFetching, isPlaceholderData, refetch } =
    useQuery<PaginatedMembersDto>({
      queryKey: ['members', page, debouncedSearch, levelId, status, tagFilter],
      queryFn: () => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(PAGE_SIZE),
          ...(debouncedSearch && { search: debouncedSearch }),
          ...(levelId && { levelId }),
          ...(status && { status }),
          ...(tagFilter.tagIds.length && {
            tagIds: tagFilter.tagIds.join(','),
            tagMatch: tagFilter.tagMatch,
            tagScope: tagFilter.tagScope,
          }),
        });
        return api.get<PaginatedMembersDto>(`/members?${params}`);
      },
      // Keep the current rows on screen while the next search loads.
      placeholderData: (previous) => previous,
    });

  const members: MemberDto[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const showAssociation = canRead && levels.some((level) => level.associationMode);
  // One rule for "a filter is active"; the empty state judges the search text actually queried.
  const hasFilters = (searchText: string) =>
    Boolean(
      searchText ||
      levelId ||
      status ||
      tagFilter.tagIds.length ||
      tagFilter.tagMatch !== DEFAULT_TAG_FILTER.tagMatch ||
      tagFilter.tagScope !== DEFAULT_TAG_FILTER.tagScope,
    );
  const filtersActive = hasFilters(search);
  const queriedWithFilters = hasFilters(debouncedSearch);
  const rangeFrom = members.length ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeTo = (page - 1) * PAGE_SIZE + members.length;

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setLevelId('');
    setStatus('');
    setTagFilter(DEFAULT_TAG_FILTER);
    setPage(1);
  };

  const outlineButton =
    'inline-flex min-h-11 items-center px-4 rounded-md border border-base-300 text-sm text-base-content hover:bg-base-300';
  const fieldClass =
    'w-full px-3 py-2 rounded-md border border-base-300 bg-base-200 text-base text-base-content focus:outline-hidden focus:ring-2 focus:ring-primary';

  const levelBadge = (level: MemberDto['level']) =>
    level ? <LevelBadge level={level} /> : <span className="text-base-content/50 text-xs">-</span>;

  const statusBadge = (value: MemberStatus) => <StatusBadge status={value} />;

  // One message for the table and the cards; null means rows are shown.
  const stateMessage = isError ? (
    <div role="alert" className="space-y-3">
      <p className="text-base text-base-content">{t('list.loadError')}</p>
      <button
        type="button"
        onClick={() => void refetch()}
        disabled={isFetching}
        className={outlineButton}
      >
        {t('list.retry')}
      </button>
    </div>
  ) : isLoading ? (
    <p className="text-base text-base-content/70">{tc('loading')}</p>
  ) : members.length === 0 ? (
    <div className="space-y-3">
      <p className="text-base text-base-content/70">
        {t(queriedWithFilters ? 'list.emptyFiltered' : 'list.emptyNone')}
      </p>
      {queriedWithFilters ? (
        <button type="button" onClick={clearFilters} className={outlineButton}>
          {t('list.clearFilters')}
        </button>
      ) : (
        <Link to="/members/create" className={outlineButton}>
          {t('createMember')}
        </Link>
      )}
    </div>
  ) : null;

  return (
    <div className="max-md:[&_button]:min-h-11 [&_button]:text-sm max-md:[&_input:not([type=checkbox])]:min-h-11 [&_input:not([type=checkbox])]:text-base max-md:[&_select]:min-h-11 [&_select]:text-base p-4 sm:p-6 max-w-7xl mx-auto">
      {(canManageNotifications || canSendNotifications) && (
        <button
          className="btn btn-outline mb-3"
          aria-expanded={showNotifications}
          onClick={() => setShowNotifications(!showNotifications)}
        >
          服務公告
        </button>
      )}
      {showNotifications && (canManageNotifications || canSendNotifications) && (
        <MemberNotificationsPanel
          key={notificationSession}
          members={members}
          canManage={canManageNotifications}
          canSend={canSendNotifications}
        />
      )}
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 mb-6">
        <h1 className="text-2xl font-bold text-base-content">{t('title')}</h1>
        <div className="flex flex-wrap gap-2">
          {canReadReservations && (
            <Link className={outlineButton} to="/members/reservations">
              {t('reservationStaffTitle')}
            </Link>
          )}
          {showAssociation && (
            <button
              type="button"
              aria-expanded={manageTags}
              aria-controls="member-tags-manage"
              onClick={() => {
                setManageTags(!manageTags);
                if (!manageTags)
                  requestAnimationFrame(() =>
                    document.getElementById('member-tags-manage')?.scrollIntoView(),
                  );
              }}
              className={outlineButton}
            >
              {t('list.manageTags')}
            </button>
          )}
          <Link to="/members/import" className={outlineButton}>
            {t('importMembers')}
          </Link>
          <button
            onClick={() => navigate('/members/create')}
            className="min-h-11 px-4 py-2 rounded-md bg-primary text-primary-content text-sm hover:bg-primary/90"
          >
            {t('createMember')}
          </button>
        </div>
      </div>

      {showAssociation && <ZipaDailyTotals />}
      {showAssociation && <MemberLevelReport />}

      {/* Filters */}
      <div className="grid grid-cols-2 gap-3 mb-4 md:flex md:flex-wrap md:items-end">
        <div className="col-span-2 md:w-72">
          <label
            htmlFor="member-search"
            className="block mb-2 text-sm font-medium text-base-content/80"
          >
            {t('list.searchLabel')}
          </label>
          <input
            id="member-search"
            type="search"
            placeholder={t('list.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className={fieldClass}
          />
        </div>

        <div className="min-w-0">
          <label
            htmlFor="member-level"
            className="block mb-2 text-sm font-medium text-base-content/80"
          >
            {t('tier')}
          </label>
          <select
            id="member-level"
            value={levelId}
            onChange={(e) => {
              setLevelId(e.target.value);
              setPage(1);
            }}
            className={fieldClass}
          >
            <option value="">{t('allLevels')}</option>
            {levels
              .filter((l) => l.isActive)
              .map((l) => (
                <option key={l.id} value={l.id}>
                  {l.displayName}
                </option>
              ))}
          </select>
        </div>

        <div className="min-w-0">
          <label
            htmlFor="member-status"
            className="block mb-2 text-sm font-medium text-base-content/80"
          >
            {t('status')}
          </label>
          <select
            id="member-status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as MemberStatus | '');
              setPage(1);
            }}
            className={fieldClass}
          >
            <option value="">{t('allStatuses')}</option>
            {(['ACTIVE', 'INACTIVE', 'BLACKLISTED'] as MemberStatus[]).map((v) => (
              <option key={v} value={v}>
                {t(`status_${v}`)}
              </option>
            ))}
          </select>
        </div>

        {showAssociation && (
          <MemberTagFilter
            filter={tagFilter}
            onFilter={(value) => {
              setTagFilter(value);
              setPage(1);
            }}
          />
        )}

        {filtersActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="col-span-2 justify-self-start min-h-11 px-2 text-sm text-primary hover:underline"
          >
            {t('list.clearFilters')}
          </button>
        )}
      </div>

      {/* Result summary */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-3">
        <p aria-live="polite" className="flex flex-wrap gap-x-3 text-sm text-base-content/70">
          {members.length > 0 && !isError && !isPlaceholderData && (
            <span>{t('list.range', { from: rangeFrom, to: rangeTo, total })}</span>
          )}
          {isFetching && !isLoading && <span>{t('list.updating')}</span>}
        </p>
        {showAssociation && (
          <MemberTagExport
            filter={tagFilter}
            exportFilter={{
              ...(debouncedSearch ? { search: debouncedSearch } : {}),
              ...(levelId ? { levelId } : {}),
              ...(status ? { status } : {}),
            }}
          />
        )}
      </div>

      <div className="md:hidden mb-4" aria-busy={isFetching}>
        {stateMessage ? (
          <div className="py-8 text-center">{stateMessage}</div>
        ) : (
          <ul className="space-y-3">
            {members.map((m) => {
              const debt = debtAmount(m.totalDebt);
              return (
                <li key={m.id}>
                  <article className="rounded-lg border border-base-300 bg-base-200 p-4 space-y-2">
                    <h2 className="text-lg font-semibold [overflow-wrap:anywhere]">{m.nickname}</h2>
                    <p className="font-mono text-xs [overflow-wrap:anywhere]">{m.memberNo}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                      {statusBadge(m.status)}
                      {levelBadge(m.level)}
                      <span>
                        {t('ledgerTabPlayer')}:{' '}
                        <span className="font-mono tabular-nums">
                          {(m.playerPoints ?? 0).toLocaleString()}
                        </span>
                      </span>
                      <span className={debt !== null ? 'text-error' : ''}>
                        {t('totalDebt')}:{' '}
                        <span className="font-mono tabular-nums">
                          {debt !== null ? `NT$${debt.toLocaleString()}` : '-'}
                        </span>
                      </span>
                      <span className="[overflow-wrap:anywhere]">
                        {t('phone')}: {m.phone ?? '-'}
                      </span>
                    </div>
                    <Link to={`/members/${m.id}`} className={outlineButton}>
                      {t('details')}
                    </Link>
                  </article>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Table */}
      <div
        className="hidden md:block bg-base-200 border border-base-300 rounded-lg overflow-x-auto"
        aria-busy={isFetching}
      >
        <table className="zipa-member-table w-full text-sm">
          <caption className="sr-only">{t('list.caption')}</caption>
          <thead className="bg-base-300">
            <tr>
              <th scope="col" className="text-left px-4 py-3 text-base-content font-medium">
                {t('memberNo')}
              </th>
              <th scope="col" className="text-left px-4 py-3 text-base-content font-medium">
                {t('nickname')}
              </th>
              <th scope="col" className="text-left px-4 py-3 text-base-content font-medium">
                {t('phone')}
              </th>
              <th scope="col" className="text-left px-4 py-3 text-base-content font-medium">
                {t('tier')}
              </th>
              <th scope="col" className="text-left px-4 py-3 text-base-content font-medium">
                {t('status')}
              </th>
              <th scope="col" className="text-right px-4 py-3 text-base-content font-medium">
                {t('ledgerTabPlayer')}
              </th>
              <th scope="col" className="text-right px-4 py-3 text-base-content font-medium">
                {t('totalDebt')}
              </th>
              <th scope="col" className="px-4 py-3">
                <span className="sr-only">{t('list.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-300">
            {stateMessage ? (
              <tr>
                <td colSpan={8} className="text-center py-10">
                  {stateMessage}
                </td>
              </tr>
            ) : (
              members.map((m) => {
                const debt = debtAmount(m.totalDebt);
                return (
                  <tr key={m.id} className="hover:bg-primary/5 transition-colors">
                    <td
                      data-label={t('memberNo')}
                      className="px-4 py-3 font-mono text-xs text-base-content/70"
                    >
                      {m.memberNo}
                    </td>
                    <td
                      data-label={t('nickname')}
                      className="px-4 py-3 font-medium text-base-content"
                    >
                      {m.nickname}
                    </td>
                    <td data-label={t('phone')} className="px-4 py-3 text-base-content/70">
                      {m.phone ?? '-'}
                    </td>
                    <td data-label={t('tier')} className="px-4 py-3">
                      {levelBadge(m.level)}
                    </td>
                    <td data-label={t('status')} className="px-4 py-3">
                      {statusBadge(m.status)}
                    </td>
                    <td
                      data-label={t('ledgerTabPlayer')}
                      className="px-4 py-3 text-right font-mono tabular-nums text-base-content"
                    >
                      {(m.playerPoints ?? 0).toLocaleString()}
                    </td>
                    <td
                      data-label={t('totalDebt')}
                      className={`px-4 py-3 text-right font-mono tabular-nums font-medium ${debt !== null ? 'text-error' : 'text-base-content'}`}
                    >
                      {debt !== null ? `NT$${debt.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-2 py-1 text-right">
                      <Link
                        to={`/members/${m.id}`}
                        className="inline-flex min-h-11 items-center px-2 text-sm text-primary hover:underline"
                      >
                        {t('details')}
                      </Link>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav aria-label={t('list.pagination')} className="flex items-center justify-end gap-2 mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || isPlaceholderData}
            className="min-h-10 px-3 rounded border border-base-300 text-sm disabled:opacity-40 hover:bg-base-300"
          >
            {t('prevPage')}
          </button>
          <span className="px-3 text-sm font-mono tabular-nums text-base-content/70">
            {isPlaceholderData ? '…' : `${page} / ${totalPages}`}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || isPlaceholderData}
            className="min-h-10 px-3 rounded border border-base-300 text-sm disabled:opacity-40 hover:bg-base-300"
          >
            {t('nextPage')}
          </button>
        </nav>
      )}

      {showAssociation && <MemberTags manageOpen={manageTags} />}
    </div>
  );
}
